import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ShieldCheck } from "lucide-react";
import { useAuth } from "../lib/auth";
import { toast } from "sonner";
import api from "../lib/api";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [mfaCode, setMfaCode] = useState("");
  const [mfaRequired, setMfaRequired] = useState(false);
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      // First call: try without MFA
      const { data } = await api.post("/auth/login", { email, password, mfa_code: mfaCode || undefined });
      if (data.mfa_required) {
        setMfaRequired(true);
        toast.info("Enter your authenticator code");
      } else {
        localStorage.setItem("mfp_token", data.token);
        localStorage.setItem("mfp_user", JSON.stringify(data.user));
        toast.success("Welcome back!");
        // Force reload to pick up auth state
        window.location.href = "/dashboard";
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex" style={{ background: "var(--mf-bg)" }}>
      <div className="hidden md:flex md:w-1/2 relative overflow-hidden items-center justify-center p-12" style={{ background: "var(--mf-primary)" }}>
        <div className="absolute inset-0 mf-noise opacity-30"></div>
        <div className="relative text-white max-w-md">
          <div className="flex items-center gap-2 mb-8">
            <div className="w-10 h-10 rounded-xl bg-white p-0.5 flex items-center justify-center">
              <img src="/icons/icon-192.png" alt="Clinician Well logo" className="w-full h-full object-cover rounded-[inherit]" />
            </div>
            <span className="font-serif text-2xl">Clinician Well</span>
          </div>
          <h2 className="font-serif text-4xl leading-tight mb-4">Calm clinical practice, powered by AI.</h2>
          <p className="opacity-85 leading-relaxed">Welcome back. Your clients are waiting — and your notes are nearly done.</p>
        </div>
      </div>

      <div className="w-full md:w-1/2 flex items-center justify-center p-6">
        <div className="w-full max-w-md mf-fade-in">
          <div className="md:hidden flex items-center gap-2 mb-10">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: "var(--mf-primary)" }}>
              <img src="/icons/icon-192.png" alt="Clinician Well logo" className="w-full h-full object-cover rounded-[inherit]" />
            </div>
            <span className="font-serif text-2xl" style={{ color: "var(--mf-primary)" }}>Clinician Well</span>
          </div>
          {!mfaRequired ? (
            <>
              <h1 className="font-serif text-4xl mb-2">Welcome back</h1>
              <p className="text-sm mb-8" style={{ color: "var(--mf-text-muted)" }}>Sign in to your clinical workspace.</p>
            </>
          ) : (
            <>
              <div className="flex items-center gap-2 mb-3">
                <ShieldCheck className="w-6 h-6" style={{ color: "var(--mf-primary)" }} />
                <h1 className="font-serif text-3xl">Two-factor required</h1>
              </div>
              <p className="text-sm mb-8" style={{ color: "var(--mf-text-muted)" }}>Enter the 6-digit code from your authenticator app.</p>
            </>
          )}

          <form onSubmit={submit} className="space-y-4">
            {!mfaRequired && (
              <>
                <div>
                  <label className="block text-sm mb-1.5 font-medium">Email</label>
                  <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required className="w-full px-4 py-3 rounded-xl outline-none text-sm" style={{ background: "var(--mf-surface)", color: "var(--mf-text)" }} data-testid="login-email-input" placeholder="you@practice.com" />
                </div>
                <div>
                  <label className="block text-sm mb-1.5 font-medium">Password</label>
                  <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} required className="w-full px-4 py-3 rounded-xl outline-none text-sm" style={{ background: "var(--mf-surface)", color: "var(--mf-text)" }} data-testid="login-password-input" />
                </div>
              </>
            )}
            {mfaRequired && (
              <div>
                <label className="block text-sm mb-1.5 font-medium">Authenticator Code</label>
                <input
                  value={mfaCode}
                  onChange={(e) => setMfaCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                  required
                  autoFocus
                  className="w-full px-4 py-3 rounded-xl outline-none text-2xl text-center font-mono tracking-widest"
                  style={{ background: "var(--mf-surface)", color: "var(--mf-text)" }}
                  data-testid="login-mfa-input"
                  placeholder="000000"
                />
              </div>
            )}
            <button type="submit" disabled={loading} className="mf-btn-primary w-full disabled:opacity-60" data-testid="login-submit-btn">
              {loading ? "Signing in…" : (mfaRequired ? "Verify" : "Sign In")}
            </button>
          </form>

          {!mfaRequired && (
            <p className="mt-6 text-sm text-center" style={{ color: "var(--mf-text-muted)" }}>
              New to Clinician Well? <Link to="/register" className="font-medium" style={{ color: "var(--mf-primary)" }} data-testid="login-register-link">Create an account</Link>
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
